import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrash, faPlus, faEdit, faEye } from '@fortawesome/free-solid-svg-icons';
import BaseURL from '../assets/contants/BaseURL';
import Header from '../components/header';
import { useNavigate } from 'react-router-dom';

const FlowMeter = () => {
    const navigate = useNavigate();
    const [successMessage, setSuccessMessage] = useState('');
    const [flowMeters, setFlowMeters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [checkedItems, setCheckedItems] = useState({});
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
    const deleteModalRef = useRef(null);
    const [dataToDisplay, setDataToDisplay] = useState(flowMeters);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setModalOpen] = useState(false);
    const [newMeter, setNewMeter] = useState({ id: '', name: '', serial: '' });
    const addModalRef = useRef(null);
    const editModalRef = useRef(null);
    const [editMeter, setEditMeter] = useState({ id: '', flow_meter_id: '', flow_meter_name: '', model: '', location: '', serial_number: '', multi_factor: '', unit_type: 'kilo' });
    const [isEditModalOpen, setEditModalOpen] = useState(false)

    const weightUnitOptions = [
        { value: 'kilo', label: 'Kilo' },
        { value: 'mega', label: 'Mega' }
    ];

    const fetchFlowMeters = async () => {
        try {
            const response = await axios.get(`${BaseURL}meters/flowmeters/`);
            setFlowMeters(response.data);
            setDataToDisplay(response.data);
            setLoading(false);
        } catch (err) {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFlowMeters();
    }, []);

    const totalPages = Math.ceil(dataToDisplay.length / itemsPerPage);
    const paginatedData = dataToDisplay.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    if (loading) {
        return <div style={{ textAlign: 'center', margin: '20px' }}>Loading...</div>;
    }

    const handleSearch = (e) => {
        const searchTerm = e.target.value.toLowerCase();
        setSearchTerm(e.target.value);
        setCurrentPage(1);
        if (searchTerm === '') {
            setDataToDisplay(flowMeters);
        } else {
            const filteredData = flowMeters.filter((item) =>
                item.flow_meter_name.toLowerCase().includes(searchTerm) ||
                item.flow_meter_id.toLowerCase().includes(searchTerm) ||
                item.model.toLowerCase().includes(searchTerm)
            );
            setDataToDisplay(filteredData.length > 0 ? filteredData : []);
        }

        setCheckedItems({});
    };

    const showSuccessMessage = (msg) => {
        setSuccessMessage(msg);
        setTimeout(() => {
            setSuccessMessage('');
        }, 3000);
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        const newCheckedItems = {};
        dataToDisplay.forEach(item => {
            newCheckedItems[item.id] = isChecked;
        });
        setCheckedItems(newCheckedItems);
    };

    const handleCheckboxChange = (id) => {
        setCheckedItems((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleDeleteMeters = async () => {
        try {
            await Promise.all(
                Object.keys(checkedItems)
                    .filter((id) => checkedItems[id])
                    .map((id) =>
                        axios.delete(`${BaseURL}meters/flowmeters/${id}/`)
                    )
            );
            fetchFlowMeters();
            setCheckedItems({});
            showSuccessMessage('Selected meters deleted successfully!');
        } catch (error) {
            console.error("Error deleting meters:", error);
        } finally {
            setDeleteModalOpen(false);
        }
    };





    const handleAddMeter = async () => {
        if (!newMeter.id || !newMeter.name || !newMeter.serial) {
            alert("Please fill in all required fields: Meter ID, Meter Name, and Serial Number.");
            return;
        }

        const meterData = {
            flow_meter_id: newMeter.id,
            flow_meter_name: newMeter.name,
            model: newMeter.model_number,
            location: newMeter.location,
            serial_number: newMeter.serial,
            multi_factor: newMeter.multi_factor,
            unit_type: newMeter.unit_type || 'kilo'
        };

        try {
            const response = await axios.get(`${BaseURL}meters/energymeters/`);
            const existingMeter = response.data.find(meter =>
                meter.flow_meter_id === newMeter.id || meter.flow_meter_name === newMeter.name
            );

            if (existingMeter) {
                alert("Flow Meter ID or Flow Meter Name already exists. Please choose a unique Meter ID or Name.");
                return;
            }

            await axios.post(`${BaseURL}meters/flowmeters/`, meterData);
            fetchFlowMeters();
            setNewMeter({ id: '', name: '', serial: '' });
            setModalOpen(false);
            showSuccessMessage('Meter added successfully!');
        } catch (error) {
            if (error.response) {
                console.error("Error adding Flow meter:", error.response.data);
                alert(`An error occurred while adding the Flow meter. Error Code: ${error.response.status}, ${error.response.data.message || error.response.data}`);
            } else if (error.request) {
                console.error("Error: No response from the server.");
                alert("No response from the server. Please try again later.");
            } else {
                console.error("Error:", error.message);
                alert(`An unexpected error occurred: ${error.message}`);
            }
        }
    };



    const handleEditMeter = async () => {

        const meterData = {
            id: editMeter.id,
            flow_meter_id: editMeter.flow_meter_id,
            flow_meter_name: editMeter.flow_meter_name,
            location: editMeter.location,
            model: editMeter.model,
            serial_number: editMeter.serial_number,
            multi_factor: editMeter.multi_factor,
            unit_type: editMeter.unit_type || 'kilo'
        };


        try {

            // console.log("Updating meter:", meterData);

            await axios.put(`${BaseURL}meters/flowmeters/${editMeter.id}/`, meterData);


            const updatedData = dataToDisplay.map((item) =>
                item.id === editMeter.id ? { ...item, ...meterData } : item
            );

            setDataToDisplay(updatedData);
            setFlowMeters((prevFullData) =>
                prevFullData.map((item) =>
                    item.id === editMeter.id ? { ...item, ...meterData } : item
                )
            );

            setEditMeter({ id: '', flow_meter_id: '', flow_meter_name: '', location: '', model: '', serial_number: '', multi_factor: '', unit_type: 'kilo' });
            setEditModalOpen(false);
            showSuccessMessage('Meter updated successfully!');
        } catch (error) {

            console.error("Error editing meter:", error.response ? error.response.data : error.message);
            alert("An error occurred while editing the meter. Please try again.");
        }
    };


    const handleEditClick = (meter) => {

        setEditMeter({
            id: meter.id,
            flow_meter_id: meter.flow_meter_id,
            flow_meter_name: meter.flow_meter_name,
            location: meter.location,
            model: meter.model,
            serial_number: meter.serial_number,
            multi_factor: meter.multi_factor,
            unit_type: meter.unit_type || 'kilo'
        });
        setEditModalOpen(true);
    };


    const handleViewDetails = (meterId, data = "meter") => {
        navigate("/EMS/FlowSub", { state: { meterId, data } });
        console.log("Meter id and data", meterId, data);
    };



    return (
        <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{
                backgroundColor: '#007acc',
                color: 'white',
                alignContent: 'center',
                fontSize: '14px',
                height: '40px'
            }}>
                <Header title="FLOW METER LIST" />
            </div>

            <div style={{
                border: '1px solid #ccc',
                padding: '10px',
                marginTop: '20px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: 'white',
                margin: '20px auto',
                maxWidth: '1000px',
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '30%' }}>
                        <input
                            type="text"
                            placeholder="Search"
                            style={{
                                padding: '10px',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                flex: '1',
                                marginRight: '10px',
                            }}
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            style={{ color: '#007acc', cursor: 'pointer' }}
                        />
                    </div>

                    <div style={{ marginLeft: "auto" }}>
                        <button
                            style={{
                                padding: "10px 15px",
                                marginRight: "10px",
                                backgroundColor: "#f44336",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                cursor: "pointer",
                            }}
                            onClick={() => {
                                const selectedMeters = Object.keys(checkedItems).filter((id) => checkedItems[id]);
                                if (selectedMeters.length > 0) {
                                    setDeleteModalOpen(true);
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faTrash} style={{ marginRight: "5px" }} />
                            Delete
                        </button>

                        <button
                            style={{
                                padding: '10px 15px',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                marginRight: '10px',
                            }}
                            onClick={() => setModalOpen(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} style={{ marginRight: '5px' }} />
                            Add
                        </button>
                    </div>
                </div>
            </div>
            <div style={{ textAlign: 'center', margin: '20px 0' }}>
                {successMessage && (
                    <div style={{
                        padding: '10px',
                        borderRadius: '5px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        margin: '10px auto',
                        width: 'fit-content',
                    }}>
                        {successMessage}
                    </div>
                )}
            </div>

            <div style={{
                marginTop: '20px',
                padding: '10px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: 'white',
                maxWidth: '1000px',
                margin: '20px auto',
                marginBottom: '70px',
            }}>
                <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#007acc', color: 'white' }}>
                        <tr>
                            <th style={{ padding: '5px' }}>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                />
                            </th>
                            <th style={{ padding: '10px' }}>S.No</th>
                            <th style={{ padding: '10px' }}>Flow Meter Name</th>
                            <th style={{ padding: '10px' }}>Flow Meter ID</th>
                            <th style={{ padding: '10px' }}>Multiplication Factor</th>
                            <th style={{ padding: '10px' }}>Model</th>
                            <th style={{ padding: '10px' }}>Location</th>
                            <th style={{ padding: '10px' }}>Installation Date</th>
                            <th style={{ padding: '10px' }}>Status</th>
                            <th style={{ padding: '10px' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((item, index) => {

                                return (
                                    <tr
                                        key={item.id}
                                        style={{
                                            backgroundColor: index % 2 === 0 ? '#f2f2f2' : '#ffffff',
                                        }}
                                    >
                                        <td style={{ padding: '10px' }}>
                                            {item.flow_meter_name !== 'Main' && (
                                                <input
                                                    type="checkbox"
                                                    checked={checkedItems[item.id] || false}
                                                    onChange={() => handleCheckboxChange(item.id)}
                                                />
                                            )}
                                        </td>
                                        <td style={{ padding: '15px' }}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td style={{ padding: '15px' }}>{item.flow_meter_name}</td>
                                        <td style={{ padding: '15px' }}>{item.flow_meter_id}</td>
                                        <td style={{ padding: '15px' }}>{item.multi_factor}</td>
                                        <td style={{ padding: '15px' }}>{item.model}</td>
                                        <td style={{ padding: '15px' }}>{item.location}</td>
                                        <td style={{ padding: '15px' }}>{item.installation_date}</td>
                                        <td style={{ padding: '15px' }}>
                                            {item.status === 1 ? 'Active' : 'Inactive'}
                                        </td>
                                        <td style={{ padding: "15px", display: 'flex', gap: '10px' }}>
                                            <span
                                                style={{ color: "blue", cursor: "pointer" }}
                                                onClick={() => handleEditClick(item)}
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </span>
                                            <span
                                                style={{ color: "green", cursor: "pointer" }}
                                                onClick={() => handleViewDetails(item.flow_meter_id)}
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="10" style={{ padding: '10px', textAlign: 'center' }}>
                                    No results found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                            margin: '0 5px',
                            padding: '5px 10px',
                            background: currentPage === 1 ? '#007acc' : '#007acc',
                            color: currentPage === 1 ? '#9e9e9e' : '#ffffff',
                            border: 'none',
                            borderRadius: '25px',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            boxShadow: currentPage === 1 ? 'none' : '0px 4px 8px rgba(0, 0, 0, 0.2)',
                            fontSize: '12px',
                        }}
                    >
                        Previous
                    </button>

                    {currentPage > 3 && (
                        <>
                            <button
                                onClick={() => handlePageChange(1)}
                                style={{
                                    margin: '0 5px',
                                    padding: '5px 10px',
                                    background: '#f7f7f7',
                                    color: '#333333',
                                    border: '1px solid #ddd',
                                    borderRadius: '25px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                }}
                            >
                                1
                            </button>
                            <span style={{ margin: '0 5px', fontSize: '14px' }}>...</span>
                        </>
                    )}
                    {Array.from({ length: 5 }, (_, i) => {
                        const pageNumber = currentPage - 2 + i;
                        if (pageNumber > 0 && pageNumber <= totalPages) {
                            return (
                                <button
                                    key={pageNumber}
                                    onClick={() => handlePageChange(pageNumber)}
                                    style={{
                                        margin: '0 5px',
                                        padding: '5px 10px',
                                        background: currentPage === pageNumber ? '#007acc' : 'linear-gradient(to right, #f7f7f7, #e0e0e0)',
                                        color: currentPage === pageNumber ? '#ffffff' : '#333333',
                                        border: '1px solid #ddd',
                                        borderRadius: '25px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                    }}
                                >
                                    {pageNumber}
                                </button>
                            );
                        }
                        return null;
                    })}
                    {currentPage < totalPages - 2 && (
                        <>
                            <span style={{ margin: '0 5px', fontSize: '14px' }}>...</span>
                            <button
                                onClick={() => handlePageChange(totalPages)}
                                style={{
                                    margin: '0 5px',
                                    padding: '5px 10px',
                                    background: '#f7f7f7',
                                    color: '#333333',
                                    border: '1px solid #ddd',
                                    borderRadius: '25px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                }}
                            >
                                {totalPages}
                            </button>
                        </>
                    )}
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{
                            margin: '0 5px',
                            padding: '5px 10px',
                            background: currentPage === totalPages ? '#007acc' : '#007acc',
                            color: currentPage === totalPages ? '#9e9e9e' : '#ffffff',
                            border: 'none',
                            borderRadius: '25px',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            boxShadow: currentPage === totalPages ? 'none' : '0px 4px 8px rgba(0, 0, 0, 0.2)',
                            fontSize: '12px',
                        }}
                    >
                        Next
                    </button>
                </div>
            </div>
            {isDeleteModalOpen && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        ref={deleteModalRef}
                        style={{
                            backgroundColor: "white",
                            padding: "20px",
                            borderRadius: "10px",
                            width: "400px",
                            textAlign: "center",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                        }}
                    >
                        <h3>Confirm Deletion</h3>
                        <p>Are you sure you want to delete the selected meters?</p>
                        <div
                            style={{
                                marginTop: "20px",
                                display: "flex",
                                justifyContent: "space-around",
                            }}
                        >
                            <div style={{ display: "flex", gap: "10px" }}>
                                <button
                                    style={{
                                        padding: "10px 15px",
                                        color: "white",
                                        backgroundColor: "#4CAF50",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                    }}
                                    onClick={handleDeleteMeters}
                                >
                                    Yes, Delete
                                </button>
                                <button
                                    style={{
                                        padding: "10px 15px",
                                        color: "white",
                                        backgroundColor: "#f44336",
                                        border: "none",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => setDeleteModalOpen(false)}
                                >
                                    Cancel
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}
            {isModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <div ref={addModalRef} style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        width: '60%',
                        maxWidth: '900px',
                        maxHeight: '70vh',
                        transition: 'all 0.3s ease',
                        overflowY: 'auto',
                    }}>
                        <h4 style={{ marginBottom: '20px', textAlign: 'center' }}>Add New Flow Meter</h4>
                        <div style={{ display: 'flex', gap: '30px' }}>
                            {/* Left Column */}
                            <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Flow Meter Name</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Flow Meter Name"
                                        value={newMeter.name}
                                        onChange={(e) => setNewMeter({ ...newMeter, name: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Flow Meter ID</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Flow Meter ID"
                                        value={newMeter.id}
                                        onChange={(e) => setNewMeter({ ...newMeter, id: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Location</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Location"
                                        value={newMeter.location}
                                        onChange={(e) => setNewMeter({ ...newMeter, location: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Unit Type</label>
                                    <select
                                        name="unit_type"
                                        value={newMeter.unit_type}
                                        onChange={(e) => setNewMeter({ ...newMeter, unit_type: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                        >
                                        {weightUnitOptions.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Model Number</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Model Number"
                                        value={newMeter.model_number}
                                        onChange={(e) => setNewMeter({ ...newMeter, model_number: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Serial Number</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Serial Number"
                                        value={newMeter.serial}
                                        onChange={(e) => setNewMeter({ ...newMeter, serial: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Mutiplication Factor</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Mutiplication Factor"
                                        value={newMeter.multi_factor}
                                        onChange={(e) => setNewMeter({ ...newMeter, multi_factor: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', width: '40%', marginLeft: '270px' }}>
                            <button
                                onClick={handleAddMeter}
                                style={{
                                    backgroundColor: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 15px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    flex: '1',
                                    marginRight: '10px',
                                }}
                            >
                                Add Meter
                            </button>
                            <button
                                onClick={() => setModalOpen(false)}
                                style={{
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 15px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    flex: '1',
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isEditModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <div ref={editModalRef} style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        width: '60%',
                        maxWidth: '900px',
                        transition: 'all 0.3s ease',
                        overflowY: 'auto',
                        maxHeight: '70vh',
                    }}>
                        <h4 style={{ marginBottom: '20px', textAlign: 'center' }}>Edit Meter</h4>
                        <div style={{ display: 'flex', gap: '30px' }}>
                            {/* Left Column */}
                            <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Flow Meter Name</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Meter Name"
                                        value={editMeter.flow_meter_name}
                                        onChange={(e) => setEditMeter({ ...editMeter, flow_meter_name: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                        disabled={editMeter.name === 'Main'}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Flow Meter ID</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Flow Meter ID"
                                        value={editMeter.flow_meter_id}
                                        onChange={(e) => setEditMeter({ ...editMeter, flow_meter_id: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Location</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Location"
                                        value={editMeter.location}
                                        onChange={(e) => setEditMeter({ ...editMeter, location: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                        disabled={editMeter.name === 'Main'}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Unit Type</label>
                                    <select
                                        name="unit_type"
                                        value={editMeter.unit_type}
                                        onChange={(e) => setEditMeter({ ...editMeter, unit_type: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                        >
                                        {weightUnitOptions.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Model Number</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Model Number"
                                        value={editMeter.model}
                                        onChange={(e) => setEditMeter({ ...editMeter, model: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                        disabled={editMeter.name === 'Main'}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Serial Number</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Serial Number"
                                        value={editMeter.serial_number}
                                        onChange={(e) => setEditMeter({ ...editMeter, serial_number: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                        disabled={editMeter.name === 'Main'}
                                    />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ flex: '1', marginRight: '10px' }}>Multiplication Factor</label>
                                    <input
                                        type="text"
                                        placeholder="Enter Multiplication Factor"
                                        value={editMeter.multi_factor}
                                        onChange={(e) => setEditMeter({ ...editMeter, multi_factor: e.target.value })}
                                        style={{ flex: '2', padding: '10px', border: '1px solid #ccc', borderRadius: '8px' }}
                                        disabled={editMeter.name === 'Main'}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', width: '40%', marginLeft: '270px' }}>
                            <button
                                onClick={handleEditMeter}
                                style={{
                                    backgroundColor: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 15px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    flex: '1',
                                    marginRight: '10px',
                                }}
                            >
                                Save Changes
                            </button>
                            <button
                                onClick={() => setEditModalOpen(false)}
                                style={{
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 15px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    flex: '1',
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FlowMeter;
